import { useState } from "react";
import { Line } from "react-chartjs-2";
import { Form, Dropdown } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import PropTypes from "prop-types";
import { getColorForMotivo } from './motivoColors';
import { getPeriodTitle } from './periodUtils';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Componente reutilizable de gráfico de líneas (Line Chart)
 * Permite visualizar evolución temporal con múltiples líneas/series
 */
function LineChart({ 
  data, 
  period,
  customDates = null,
  title = "Evolución Temporal",
  height = 400,
  legendPosition = "right",
  showFilter = true,
  filterLabel = "Series",
  defaultSelectedCount = 4,
  noDataMessage = "No hay datos disponibles para el período seleccionado",
  noSelectionMessage = "Selecciona al menos una serie para visualizar el gráfico",
  stepSize = 20,
  tension = 0.4,
  useConsistentColors = true
}) {
  // Estado para las series seleccionadas
  const [seriesSeleccionadas, setSeriesSeleccionadas] = useState([]);
  // Estado para controlar si el dropdown está abierto
  const [dropdownAbierto, setDropdownAbierto] = useState(false);

  // Detectar si es móvil
  const isMobile = window.innerWidth < 768;
  const finalLegendPosition = isMobile ? 'bottom' : legendPosition;

  // Inicializar series seleccionadas con las primeras N cuando cambien los datos
  if (data?.datasets && seriesSeleccionadas.length === 0 && data.datasets.length > 0) {
    const primerasN = data.datasets.slice(0, Math.min(defaultSelectedCount, data.datasets.length)).map(d => d.label);
    setSeriesSeleccionadas(primerasN);
  }

  // Filtrar datasets según series seleccionadas
  const datasetsFiltrados = data?.datasets?.filter(dataset => 
    seriesSeleccionadas.includes(dataset.label)
  ) || [];

  // Manejar cambio en checkboxes
  const handleCheckboxChange = (serie) => {
    if (seriesSeleccionadas.includes(serie)) {
      setSeriesSeleccionadas(seriesSeleccionadas.filter(s => s !== serie));
    } else {
      setSeriesSeleccionadas([...seriesSeleccionadas, serie]);
    }
  };

  // Verificar si todos están seleccionados
  const todosSeleccionados = data?.datasets && seriesSeleccionadas.length === data.datasets.length;

  // Seleccionar/deseleccionar todos
  const toggleTodos = () => {
    if (todosSeleccionados) {
      setSeriesSeleccionadas([]);
    } else {
      setSeriesSeleccionadas(data.datasets.map(d => d.label));
    }
  };

  // Paleta de colores para asignar por índice cuando no se usa mapeo consistente
  const colorPalette = [
    { border: "rgb(54, 162, 235)", background: "rgba(54, 162, 235, 0.1)" },   // Azul
    { border: "rgb(153, 102, 255)", background: "rgba(153, 102, 255, 0.1)" }, // Púrpura
    { border: "rgb(75, 192, 192)", background: "rgba(75, 192, 192, 0.1)" },   // Turquesa
    { border: "rgb(34, 197, 94)", background: "rgba(34, 197, 94, 0.1)" },     // Verde
    { border: "rgb(255, 159, 64)", background: "rgba(255, 159, 64, 0.1)" },   // Naranja
    { border: "rgb(255, 99, 132)", background: "rgba(255, 99, 132, 0.1)" },   // Rosa/Rojo
    { border: "rgb(255, 205, 86)", background: "rgba(255, 205, 86, 0.1)" },   // Amarillo
    { border: "rgb(201, 203, 207)", background: "rgba(201, 203, 207, 0.1)" }, // Gris
    { border: "rgb(23, 162, 184)", background: "rgba(23, 162, 184, 0.1)" },   // Cian
    { border: "rgb(220, 53, 69)", background: "rgba(220, 53, 69, 0.1)" },     // Rojo
  ];

  const chartData = {
    labels: data?.labels || [],
    datasets: datasetsFiltrados.map((dataset, index) => {
      // Obtener color consistente si está habilitado
      let color;
      if (useConsistentColors) {
        color = getColorForMotivo(dataset.label);
      } else {
        // Si vienen colores en los datos, usarlos; sino asignar por índice de la paleta
        if (dataset.borderColor && dataset.backgroundColor) {
          color = {
            border: dataset.borderColor,
            background: dataset.backgroundColor
          };
        } else {
          // Encontrar el índice original en todos los datasets para mantener consistencia en el filtrado
          const originalIndex = data.datasets.findIndex(d => d.label === dataset.label);
          color = colorPalette[originalIndex % colorPalette.length];
        }
      }
      
      return {
        label: dataset.label,
        data: dataset.data,
        borderColor: color.border,
        backgroundColor: color.background.replace('0.8', '0.1'), // Hacer más transparente para líneas
        borderWidth: 2,
        tension: tension,
        pointRadius: isMobile ? 3 : 4,
        pointHoverRadius: isMobile ? 5 : 6,
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: finalLegendPosition,
        labels: {
          usePointStyle: true,
          padding: isMobile ? 10 : 15,
          font: {
            size: isMobile ? 10 : 12,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += " : ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y;
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11,
          },
          stepSize: stepSize,
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1">{title}</h5>
          {getPeriodTitle(period, customDates) && (
            <p className="text-muted small mb-0">
              {getPeriodTitle(period, customDates)}
            </p>
          )}
        </div>

        {/* Dropdown selector de series */}
        {showFilter && data?.datasets && data.datasets.length > 0 && (
          <Dropdown
            show={dropdownAbierto}
            onToggle={(isOpen) => setDropdownAbierto(isOpen)}
          >
            <Dropdown.Toggle
              size="sm"
              id="dropdown-series"
              className="d-flex align-items-center gap-2 btn-outline-primary-blue"
            >
              <i className="bi bi-funnel"></i>
              {filterLabel} ({seriesSeleccionadas.length})
            </Dropdown.Toggle>

            <Dropdown.Menu
              style={{
                minWidth: "300px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <div className="px-3 py-2">
                <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                  <small className="text-muted fw-semibold">
                    Seleccionar {filterLabel.toLowerCase()}
                  </small>
                  <button
                    className="btn btn-link btn-sm text-decoration-none p-0"
                    onClick={toggleTodos}
                    style={{ fontSize: "0.85rem" }}
                  >
                    {todosSeleccionados ? "Limpiar" : "Todos"}
                  </button>
                </div>

                {data.datasets.map((dataset, index) => (
                  <div key={dataset.label} className="py-1">
                    <Form.Check
                      type="checkbox"
                      id={`serie-dropdown-${index}`}
                      label={dataset.label}
                      checked={seriesSeleccionadas.includes(dataset.label)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange(dataset.label);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="user-select-none"
                    />
                  </div>
                ))}

                {seriesSeleccionadas.length === 0 && (
                  <small className="text-danger d-block mt-2 pt-2 border-top">
                    Selecciona al menos una opción
                  </small>
                )}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      <div style={{ height: `${height}px`, position: "relative" }}>
        {data && data.datasets && data.datasets.length > 0 ? (
          seriesSeleccionadas.length > 0 ? (
            <Line data={chartData} options={options} />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="text-center">
                <i className="bi bi-graph-up fs-1 text-muted"></i>
                <p className="text-muted mt-2">{noSelectionMessage}</p>
              </div>
            </div>
          )
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center">
              <i className="bi bi-graph-up fs-1 text-muted"></i>
              <p className="text-muted mt-2">{noDataMessage}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

LineChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
        borderColor: PropTypes.string,
        backgroundColor: PropTypes.string,
      })
    ),
  }),
  period: PropTypes.string.isRequired,
  customDates: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  title: PropTypes.string,
  height: PropTypes.number,
  legendPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  showFilter: PropTypes.bool,
  filterLabel: PropTypes.string,
  defaultSelectedCount: PropTypes.number,
  noDataMessage: PropTypes.string,
  noSelectionMessage: PropTypes.string,
  stepSize: PropTypes.number,
  tension: PropTypes.number,
  useConsistentColors: PropTypes.bool,
};

export default LineChart;
